import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import SyncProblemOutlined from '@mui/icons-material/SyncProblemOutlined';
import {
  Box,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import CustomButton from '../../../components/@extended/CustomButton';
import CustomCard from '../../../components/@extended/CustomCard';
import { CustomTableFoot, CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../../components/@extended/CustomTable';
import CustomTablePagination from '../../../components/common/TablePagination';
import { active, pending } from '../../../constants/bankAccountStatus';
import {
  useGetSiteSubjectsQuery,
  useRequestVerificationMutation,
} from '../../../store/slices/apiSlice';
import { openMessage } from '../../../store/slices/showMessageSlice';
import OnboardSubjectDialog from './OnboardSubjectDialog';

const columns = [
  { id: 'name', label: 'Name', minWidth: 140, align: 'left' },
  { id: 'email', label: 'Email', minWidth: 180, align: 'left' },
  { id: 'accountStatus', label: 'Account Status', minWidth: 130, align: 'center' },
  { id: 'linkedAt', label: 'Linked Date', minWidth: 120, align: 'center' },
  { id: 'action', label: '', minWidth: 60, align: 'center' },
];

const parseSubjectName = (subjectEntity) => {
  if (!subjectEntity?.addditionalInfo) return subjectEntity?.email || 'N/A';
  try {
    const info = JSON.parse(subjectEntity.addditionalInfo);
    return info?.name || subjectEntity.email || 'N/A';
  } catch {
    return subjectEntity?.email || 'N/A';
  }
};

const formatStatusLabel = (status) => {
  if (!status) return 'Unknown';
  return status[0].toUpperCase() + status.slice(1);
};

const SiteSubjectsCard = ({
  protocolNumber,
  siteEntityId,
  studySiteMappingId,
  isSponsor = false,
  onSubjectsChanged,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openOnboard, setOpenOnboard] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 5 });
  const [requestVerification, { isLoading: verificationLoading }] = useRequestVerificationMutation();

  const skip = !protocolNumber || !siteEntityId || !studySiteMappingId;
  const { data, isLoading, isFetching, refetch } = useGetSiteSubjectsQuery(
    {
      protocolNumber,
      siteEntityId,
      studySiteMappingId,
      page: pagination.page + 1,
      limit: pagination.rowsPerPage,
    },
    { skip, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [protocolNumber, siteEntityId, studySiteMappingId]);

  const rows = useMemo(
    () =>
      (data?.subjects ?? []).map((entry) => ({
        id: entry.id,
        name: parseSubjectName(entry.subjectEntity),
        email: entry.subjectEntity?.email || 'N/A',
        accountStatus: entry.subjectEntity?.accountStatus,
        entityId: entry.subjectEntity?.id,
        linkedAt: entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : '-',
      })),
    [data?.subjects]
  );

  const onNewPage = (page, rowsPerPage) => {
    setPagination({ page, rowsPerPage });
  };

  const handleRefetch = () => {
    refetch();
    onSubjectsChanged?.();
  };

  const handleOnboardSuccess = () => {
    refetch();
    onSubjectsChanged?.();
  };

  const handleRequestVerification = async (entityId, email) => {
    try {
      const res = await requestVerification(entityId).unwrap();
      if (res?.success) {
        dispatch(
          openMessage({
            messageSeverity: 'success',
            message: `Requested verification for ${email}`,
          })
        );
      }
    } catch {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: `Failed to request verification for ${email}`,
        })
      );
    }
  };

  const headerActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isSponsor && (
        <>

          <CustomButton variant="contained" size="small" onClick={() => setOpenOnboard(true)}>
            Onboard Subject
          </CustomButton>
        </>
      )}
    </Box>
  );

  return (
    <>
      <CustomCard title={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>Subjects</Typography><Tooltip title="Refresh subjects">
        <IconButton size="small" aria-label="refresh-subjects" onClick={handleRefetch} disabled={isFetching || skip}>
          {isFetching ? <CircularProgress size={18} /> : <RefreshOutlined fontSize="small" />}
        </IconButton>
      </Tooltip></Box>} action={headerActions}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader aria-label="sticky table">
              <CustomTableHead>
                <TableRow>
                  {columns.map((col) => (
                    <CustomTableHeadCell key={col.id} align={col.align} sx={{ minWidth: col.minWidth }}>
                      {col.label}
                    </CustomTableHeadCell>
                  ))}
                </TableRow>
              </CustomTableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Box sx={{ py: 3 }}>
                        <CircularProgress size={28} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : rows.length > 0 ? (
                  rows.map((row) => (
                    <CustomTableRow key={row.id}>
                      <TableCell>
                        <Tooltip title={row.name}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 160,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.email}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.email}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: pending.includes(row.accountStatus)
                            ? theme.palette.warning.main
                            : active.includes(row.accountStatus)
                              ? theme.palette.success.main
                              : theme.palette.text.primary,
                          fontWeight: 600,
                        }}
                      >
                        {formatStatusLabel(row.accountStatus)}
                        {pending.includes(row.accountStatus) && row.entityId && (
                          <Tooltip title="Onboarding/KYC pending: Request verification">
                            <IconButton
                              size="small"
                              aria-label="request-verification"
                              disabled={verificationLoading}
                              onClick={() => handleRequestVerification(row.entityId, row.email)}
                              sx={{ color: theme.palette.warning.main, ml: 0.5 }}
                            >
                              <SyncProblemOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">{row.linkedAt}</TableCell>
                      <TableCell align="center" />
                    </CustomTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No subjects linked to this site yet.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <CustomTableFoot>
                <CustomTableRow sx={{ right: 0 }}>
                  <CustomTableHeadCell colSpan={columns.length}>
                    <CustomTablePagination
                      page={pagination.page}
                      rowsPerPage={pagination.rowsPerPage}
                      count={data?.pagination?.total ?? 0}
                      onNewPage={onNewPage}
                    />
                  </CustomTableHeadCell>
                </CustomTableRow>
              </CustomTableFoot>
            </Table>
          </TableContainer>
        </CardContent>
      </CustomCard>

      {isSponsor && (
        <OnboardSubjectDialog
          open={openOnboard}
          onClose={() => setOpenOnboard(false)}
          protocolNumber={protocolNumber}
          siteEntityId={siteEntityId}
          onSuccess={handleOnboardSuccess}
        />
      )}
    </>
  );
};

export default SiteSubjectsCard;
